import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { ArrowLeft, Play, Pause, RotateCcw } from "lucide-react";

const BoxBreathingPage = () => {
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState("ready"); // ready, inhale, hold1, exhale, hold2
  const [count, setCount] = useState(4);
  const [cycles, setCycles] = useState(0);
  const timerRef = useRef(null);
  const countRef = useRef(4);
  const phaseRef = useRef("ready");

  const PHASE_DURATION = 4; // 4 seconds per phase

  const PHASE_MESSAGES = {
    ready: "Press play to begin",
    inhale: "Breathe In",
    hold1: "Hold",
    exhale: "Breathe Out",
    hold2: "Hold"
  };

  const PHASE_COLORS = {
    ready: "#6B4E8A",
    inhale: "#8DB5A0",   // sage green
    hold1: "#D4C5E8",    // light lavender — distinct from inhale
    exhale: "#7AA38F",   // deeper sage
    hold2: "#F0ECF7"     // lavender mist
  };

  const getNextPhase = (current) => {
    switch (current) {
      case "ready": return "inhale";
      case "inhale": return "hold1";
      case "hold1": return "exhale";
      case "exhale": return "hold2";
      case "hold2": return "inhale";
      default: return "ready";
    }
  };

  // Single timer that handles both countdown and phase transitions
  const tick = useCallback(() => {
    countRef.current -= 1;
    
    if (countRef.current <= 0) {
      // Time to move to next phase
      const currentPhase = phaseRef.current;
      const next = getNextPhase(currentPhase);
      
      if (currentPhase === "hold2") {
        setCycles(c => c + 1);
      }
      
      phaseRef.current = next;
      countRef.current = PHASE_DURATION;
      setPhase(next);
      setCount(PHASE_DURATION);
    } else {
      setCount(countRef.current);
    }
  }, []);

  // Start/stop the single interval
  useEffect(() => {
    if (isActive && phase !== "ready") {
      timerRef.current = setInterval(tick, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isActive, phase, tick]);

  const handleStart = () => {
    phaseRef.current = "inhale";
    countRef.current = PHASE_DURATION;
    setPhase("inhale");
    setCount(PHASE_DURATION);
    setIsActive(true);
  };

  const handlePause = () => {
    setIsActive(false);
  };

  const handleResume = () => {
    setIsActive(true);
  };

  const handleReset = () => {
    setIsActive(false);
    phaseRef.current = "ready";
    countRef.current = PHASE_DURATION;
    setPhase("ready");
    setCount(PHASE_DURATION);
    setCycles(0);
  };

  const getCircleScale = () => {
    switch (phase) {
      case "inhale":
        return 1.5;
      case "hold1":
        return 1.5;
      case "exhale":
        return 1;
      case "hold2":
        return 1;
      default:
        return 1;
    }
  };

  return (
    <div className="min-h-screen bg-[#6B4E8A] text-white" data-testid="box-breathing-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 p-6">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <Link 
            to="/dashboard" 
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="min-h-screen flex flex-col items-center justify-center p-6">
        {/* Circle Animation */}
        <div className="relative mb-12">
          {/* Outer glow ring */}
          <motion.div
            animate={{
              scale: getCircleScale(),
              opacity: isActive ? [0.3, 0.5, 0.3] : 0.3
            }}
            transition={{
              scale: { duration: PHASE_DURATION, ease: "easeInOut" },
              opacity: { duration: 2, repeat: Infinity, ease: "easeInOut" }
            }}
            className="absolute inset-0 rounded-full"
            style={{
              width: 280,
              height: 280,
              background: `radial-gradient(circle, ${PHASE_COLORS[phase]}40 0%, transparent 70%)`,
              filter: "blur(20px)"
            }}
          />

          {/* Main circle */}
          <motion.div
            animate={{
              scale: getCircleScale()
            }}
            transition={{
              duration: PHASE_DURATION,
              ease: "easeInOut"
            }}
            className="relative w-64 h-64 rounded-full flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${PHASE_COLORS[phase]} 0%, ${PHASE_COLORS[phase]}80 100%)`,
              boxShadow: `0 0 60px ${PHASE_COLORS[phase]}50`
            }}
          >
            {/* Inner content */}
            <div className="text-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={phase}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <p className="text-white/80 text-sm uppercase tracking-widest mb-2">
                    {PHASE_MESSAGES[phase]}
                  </p>
                  {phase !== "ready" && (
                    <p className="text-5xl font-light" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {count}
                    </p>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>

        {/* Cycles Counter */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-8 text-center"
        >
          <p className="text-white/50 text-sm">Cycles completed</p>
          <p className="text-3xl font-light" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {cycles}
          </p>
        </motion.div>

        {/* Controls */}
        <div className="flex items-center gap-4">
          {!isActive ? (
            <Button
              onClick={phase === "ready" ? handleStart : handleResume}
              className="bg-white text-[#6B4E8A] hover:bg-white/90 rounded-full px-8 py-6 text-lg"
              data-testid="start-breathing-btn"
            >
              <Play className="w-5 h-5 mr-2" />
              {phase === "ready" ? "Start" : "Resume"}
            </Button>
          ) : (
            <Button
              onClick={handlePause}
              className="bg-white/20 text-white hover:bg-white/30 rounded-full px-8 py-6 text-lg"
              data-testid="pause-breathing-btn"
            >
              <Pause className="w-5 h-5 mr-2" />
              Pause
            </Button>
          )}
          {(phase !== "ready" || cycles > 0) && (
            <Button
              onClick={handleReset}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 rounded-full px-6 py-6"
              data-testid="reset-breathing-btn"
            >
              <RotateCcw className="w-5 h-5" />
            </Button>
          )}
        </div>

        {/* Instructions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-16 max-w-md text-center"
        >
          <h2 className="text-xl font-medium mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Box Breathing Technique
          </h2>
          <p className="text-white/60 text-sm leading-relaxed mb-6">
            Box breathing is a powerful relaxation technique used by Navy SEALs and healthcare professionals. 
            Follow the 4-4-4-4 pattern: breathe in for 4 seconds, hold for 4 seconds, breathe out for 4 seconds, 
            and hold for 4 seconds.
          </p>
          <div className="grid grid-cols-4 gap-2 text-xs">
            <div className="p-3 rounded-xl bg-[#8DB5A0]/20">
              <div className="w-3 h-3 rounded-full bg-[#8DB5A0] mx-auto mb-2"></div>
              <p className="text-white/80">Inhale</p>
              <p className="text-white/50">4 sec</p>
            </div>
            <div className="p-3 rounded-xl bg-[#8DB5A0]/20">
              <div className="w-3 h-3 rounded-full bg-[#8DB5A0] mx-auto mb-2"></div>
              <p className="text-white/80">Hold</p>
              <p className="text-white/50">4 sec</p>
            </div>
            <div className="p-3 rounded-xl bg-[#10B981]/20">
              <div className="w-3 h-3 rounded-full bg-[#10B981] mx-auto mb-2"></div>
              <p className="text-white/80">Exhale</p>
              <p className="text-white/50">4 sec</p>
            </div>
            <div className="p-3 rounded-xl bg-[#F0ECF7]/20">
              <div className="w-3 h-3 rounded-full bg-[#F0ECF7] mx-auto mb-2"></div>
              <p className="text-white/80">Hold</p>
              <p className="text-white/50">4 sec</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BoxBreathingPage;
